import { supabase } from './api'

/**
 * The KPI scorecard — personal accountability numbers, mirroring 90.io.
 *
 * Everyone reads every metric, same visibility rule as Issues and the PDF
 * scorecard (0009/0011): the floor team follows this together in the L10
 * meeting. Entering a number is restricted to the assigned technician or an
 * admin correcting it — see 0015's header for the full reasoning.
 *
 * Not every metric is a plain number (0016): a metric also has a
 * `value_type` (number / percentage / boolean) and a `cadence` (daily /
 * weekly). All three value types still store a plain number underneath —
 * percentage as a whole number like 85, boolean as 0/1 — so the red/green
 * "met target" logic needs no branching; only display and the admin form
 * care which one it is.
 */

export type KpiDirection = 'higher_is_better' | 'lower_is_better'
export type KpiValueType = 'number' | 'percentage' | 'boolean'
export type KpiCadence = 'daily' | 'weekly'

export interface KpiHistoryPoint {
  period_start: string // YYYY-MM-DD, shop-local — that day (daily) or that week's Monday (weekly)
  value: number
  met: boolean
}

export interface KpiMetric {
  metric_id: string
  name: string
  value_type: KpiValueType
  cadence: KpiCadence
  target: number
  direction: KpiDirection
  employee_id: string
  employee_name: string
  /** True when the signed-in person is the one this metric is assigned to. */
  mine: boolean
  archived: boolean
  period_start: string
  /** This period's number, or null if nobody has entered it yet. */
  current_value: number | null
  /**
   * Whether the current number clears the target. Null (not false) when
   * nothing has been entered yet — the UI needs to tell "not entered" apart
   * from "entered and missed."
   */
  current_met: boolean | null
  /** Trailing prior periods, newest first. Does not include the current one. */
  history: KpiHistoryPoint[]
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') return new Error(error.message || 'That value was rejected.')
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function listKpiMetrics(
  token: string,
  includeArchived = false,
): Promise<KpiMetric[]> {
  const { data, error } = await supabase.rpc('list_kpi_metrics', {
    p_token: token,
    p_include_archived: includeArchived,
  })
  if (error) throw fail(error)
  return (data as KpiMetric[] | null) ?? []
}

/**
 * Enter or correct a number. Defaults to the current period (today for a
 * daily metric, this week for a weekly one) — pass a `periodStart` only for
 * a deliberate back-dated correction. The database normalizes whatever date
 * is passed to that metric's own period boundary and upserts, so calling
 * this twice in the same period corrects rather than duplicating.
 */
export async function setKpiValue(
  token: string,
  metricId: string,
  value: number,
  periodStart?: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_kpi_value', {
    p_token: token,
    p_metric_id: metricId,
    p_value: value,
    p_period_start: periodStart ?? null,
  })
  if (error) throw fail(error)
}

export interface KpiMetricDraft {
  name: string
  employeeId: string
  target: number
  direction: KpiDirection
  valueType: KpiValueType
  cadence: KpiCadence
}

export async function adminCreateKpiMetric(
  token: string,
  draft: KpiMetricDraft,
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_create_kpi_metric', {
    p_token: token,
    p_name: draft.name,
    p_employee_id: draft.employeeId,
    p_target: draft.target,
    p_direction: draft.direction,
    p_value_type: draft.valueType,
    p_cadence: draft.cadence,
  })
  if (error) throw fail(error)
  return data as string
}

export async function adminUpdateKpiMetric(
  token: string,
  metricId: string,
  draft: KpiMetricDraft,
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_kpi_metric', {
    p_token: token,
    p_metric_id: metricId,
    p_name: draft.name,
    p_employee_id: draft.employeeId,
    p_target: draft.target,
    p_direction: draft.direction,
    p_value_type: draft.valueType,
    p_cadence: draft.cadence,
  })
  if (error) throw fail(error)
}

export async function adminArchiveKpiMetric(
  token: string,
  metricId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_kpi_metric', {
    p_token: token,
    p_metric_id: metricId,
    p_archived: archived,
  })
  if (error) throw fail(error)
}

/** Permanent. Takes every number ever entered against the metric with it (0024). */
export async function adminDeleteKpiMetric(token: string, metricId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_kpi_metric', {
    p_token: token,
    p_metric_id: metricId,
  })
  if (error) throw fail(error)
}
